import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Tray } from '../Tray/Tray';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type {
  UserResource,
  UserResourceState,
  ResourceCategory,
  OfferType,
  ResourcePhoto,
} from '../../Data/UserResources/userResourceTypes';
import {
  RESOURCE_CATEGORIES,
  OFFER_TYPES,
  USER_RESOURCE_STATES,
  USER_RESOURCE_TRANSITIONS,
  isValidUserResourceTransition,
} from '../../Data/UserResources/userResourceTypes';
import { communityMembershipStore } from '../../Data/store';
import { resolveCommunityPathNames } from '../../Data/ProjectConfig/projectConfigOverrides';
import styles from './UserResourceRegistry.module.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface UserResourceRegistryProps {
  userGuid: string;
  resources: UserResource[];
  onResourcesChange: (updated: UserResource[]) => void;
  onSave?: (resource: UserResource) => Promise<void>;
  onDelete?: (resourceGuid: string) => Promise<void>;
  onResourceSelect?: (resource: UserResource | null) => void;
  selectedResourceGuid?: string | null;
  canEdit?: boolean;
  /**
   * Hide the component's own header bar (icon + title + count + Add). Used
   * when a host (the Resources Rail) supplies that chrome itself — the app
   * header already names the section, and the Add action + collapse toggle
   * live in the Rail header row. The host triggers Add via the imperative
   * handle's openAdd().
   */
  hideHeader?: boolean;
}

interface EditingResource extends Partial<UserResource> {
  tempId?: string;
}

/** Lightweight community tuple for the sharing checklist. */
interface CommunityOption {
  guid: string;
  name: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto?.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Alphabetized category list for dropdown. */
function useSortedCategories(tKpi: (key: string) => string): ResourceCategory[] {
  return useMemo(() => {
    return [...RESOURCE_CATEGORIES].sort((a, b) =>
      tKpi(`preflight.categories.${a}`).localeCompare(tKpi(`preflight.categories.${b}`))
    );
  }, [tKpi]);
}

/** Compress an image file to a data URL under maxBytes. */
async function compressImage(file: File, maxBytes = 250_000): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        // Scale down if needed
        const MAX_DIM = 1200;
        if (width > MAX_DIM || height > MAX_DIM) {
          const scale = MAX_DIM / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas not supported')); return; }
        ctx.drawImage(img, 0, 0, width, height);
        // Try progressively lower quality
        for (let q = 0.85; q >= 0.3; q -= 0.1) {
          const dataUrl = canvas.toDataURL('image/jpeg', q);
          if (dataUrl.length <= maxBytes * 1.37) { // base64 overhead
            resolve(dataUrl);
            return;
          }
        }
        resolve(canvas.toDataURL('image/jpeg', 0.3));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ================================================================== */
/*  ResourceEditModal                                                  */
/* ================================================================== */

interface ResourceEditModalProps {
  resource: EditingResource | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (resource: UserResource) => void;
  onDelete?: (resourceGuid: string) => void;
  userGuid: string;
  communities: CommunityOption[];
  t: (key: string) => string;
  tKpi: (key: string) => string;
}

/** Status states shown in the modal (excluding committed — that's system-managed). */
const MODAL_STATES: UserResourceState[] = ['available', 'reserved', 'unavailable', 'retired'];

function ResourceEditModal({
  resource,
  isOpen,
  onClose,
  onSave,
  onDelete,
  userGuid,
  communities,
  t,
  tKpi,
}: ResourceEditModalProps) {
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Reset delete confirmation when modal opens/closes
  useEffect(() => {
    if (isOpen) setDeleteConfirm(false);
  }, [isOpen]);
  const sortedCategories = useSortedCategories(tKpi);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ── Collapsible section state ── */
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    info: true,
    sharing: true,
    photos: false,
    lendingTerms: false,
  });

  const toggleSection = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ── Form data ── */
  const makeDefault = useCallback(
    (): EditingResource => ({
      tempId: generateUUID(),
      user_guid: userGuid,
      label: '',
      description: '',
      category: sortedCategories[0] as ResourceCategory,
      offer_type: 'loan' as OfferType,
      state: 'available' as UserResourceState,
      shared_communities: [],
      photos: [],
      emergency_available: false,
      emergency_only: false,
      deployment_time_minutes: null,
      deployment_time_unit: 'minutes',
      emergency_notes: null,
      certification_number: null,
      certification_date: null,
      certification_expiry: null,
      conditions: '',
      sort_order: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }),
    [userGuid, sortedCategories]
  );

  const [formData, setFormData] = useState<EditingResource>(resource || makeDefault());

  useEffect(() => {
    if (isOpen) {
      setFormData(resource || makeDefault());
      // When editing an existing resource with emergency fields, open that section
      if (resource?.emergency_available) {
        setOpenSections((prev) => ({ ...prev, emergency: true }));
      }
      // Certification fields are inline (not a collapsible section),
      // so no section state needed — they appear automatically when
      // category === 'certifications'.
    }
  }, [resource, isOpen, makeDefault]);

  /* ── Photo drag-and-drop ── */
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const dragSrcIdx = useRef<number | null>(null);

  const handlePhotoDragStart = (idx: number) => { dragSrcIdx.current = idx; };
  const handlePhotoDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };
  const handlePhotoDrop = (idx: number) => {
    const src = dragSrcIdx.current;
    if (src === null || src === idx) { setDragOverIdx(null); return; }
    setFormData((prev) => {
      const photos = [...(prev.photos || [])];
      const [moved] = photos.splice(src, 1);
      photos.splice(idx, 0, moved);
      return { ...prev, photos: photos.map((p, i) => ({ ...p, sort_order: i })) };
    });
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  };

  const handlePhotoAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const currentPhotos = formData.photos || [];
    if (currentPhotos.length >= 3) return;

    const slotsAvailable = 3 - currentPhotos.length;
    const toProcess = Array.from(files).slice(0, slotsAvailable);

    const newPhotos: ResourcePhoto[] = [];
    for (const file of toProcess) {
      try {
        const url = await compressImage(file);
        newPhotos.push({ url, sort_order: currentPhotos.length + newPhotos.length });
      } catch {
        // Skip failed compressions
      }
    }

    setFormData((prev) => ({
      ...prev,
      photos: [...(prev.photos || []), ...newPhotos],
    }));
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePhotoRemove = (idx: number) => {
    setFormData((prev) => ({
      ...prev,
      photos: (prev.photos || [])
        .filter((_, i) => i !== idx)
        .map((p, i) => ({ ...p, sort_order: i })),
    }));
  };

  /* ── Community sharing ── */
  const toggleCommunity = (guid: string) => {
    setFormData((prev) => {
      const current = prev.shared_communities || [];
      const has = current.some((c) => c.toUpperCase() === guid.toUpperCase());
      return {
        ...prev,
        shared_communities: has
          ? current.filter((c) => c.toUpperCase() !== guid.toUpperCase())
          : [...current, guid],
      };
    });
  };

  const selectAllCommunities = () => {
    setFormData((prev) => ({
      ...prev,
      shared_communities: communities.map((c) => c.guid),
    }));
  };

  const deselectAllCommunities = () => {
    setFormData((prev) => ({ ...prev, shared_communities: [] }));
  };

  const isCommunitySelected = (guid: string) =>
    (formData.shared_communities || []).some((c) => c.toUpperCase() === guid.toUpperCase());

  /* ── Submit ── */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.label?.trim()) return;

    const resourceToSave: UserResource = {
      resource_guid: formData.resource_guid || generateUUID(),
      user_guid: formData.user_guid || userGuid,
      label: formData.label || '',
      description: formData.description || '',
      category: (formData.category || sortedCategories[0]) as ResourceCategory,
      offer_type: (formData.offer_type || 'loan') as OfferType,
      state: (formData.state || 'available') as UserResourceState,
      shared_communities: formData.shared_communities || [],
      photos: formData.photos || [],
      emergency_available: formData.emergency_available ?? false,
      emergency_only: formData.emergency_only ?? false,
      deployment_time_minutes: formData.deployment_time_minutes ?? null,
      deployment_time_unit: formData.deployment_time_unit || 'minutes',
      emergency_notes: formData.emergency_notes || null,
      certification_number: formData.category === 'certifications' ? (formData.certification_number || null) : null,
      certification_date: formData.category === 'certifications' ? (formData.certification_date || null) : null,
      certification_expiry: formData.category === 'certifications' ? (formData.certification_expiry || null) : null,
      conditions: formData.conditions || '',
      sort_order: formData.sort_order || 0,
      created_at: formData.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      // Legacy fields — send defaults
      descriptor_tags: [],
      availability_type: formData.offer_type === 'donation' ? 'one_time' : 'ongoing',
      reach: 'local',
      visibility: 'community',
      location_entity_guid: null,
      quantity: null,
      unit: null,
    };

    onSave(resourceToSave);
    onClose();
  };

  if (!isOpen) return null;

  /* ── Section renderer ── */
  const renderSection = (
    key: string,
    labelKey: string,
    isEmergency: boolean,
    children: React.ReactNode,
    required?: boolean
  ) => (
    <div>
      <button
        type="button"
        className={`${styles.sectionHeader} ${isEmergency ? styles.sectionHeaderEmergency : ''}`}
        onClick={() => toggleSection(key)}
        aria-expanded={openSections[key]}
      >
        <span className={`${styles.sectionChevron} ${openSections[key] ? styles.sectionChevronOpen : ''}`}>
          ›
        </span>
        <span className={`${styles.sectionLabel} ${isEmergency ? styles.sectionLabelEmergency : ''}`}>
          {t(labelKey)}
        </span>
        {required && <span className={styles.sectionRequired}>*</span>}
      </button>
      {openSections[key] && <div className={styles.sectionContent}>{children}</div>}
    </div>
  );

  return (
    <Tray
      open={isOpen}
      onClose={onClose}
      title={resource?.resource_guid ? t('resources.editResource') : t('resources.addResource')}
      width={560}
    >
      <Tray.Body>
        <form id="resourceEditForm" onSubmit={handleSubmit} className={styles.modalForm}>
          {/* ── Section: Resource Info ── */}
          {renderSection('info', 'resources.sectionResourceInfo', false, (
            <>
              {/* Resource Name */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="label">
                  {t('resources.label')}
                </label>
                <input
                  id="label"
                  type="text"
                  className={styles.formInput}
                  placeholder={t('resources.labelPlaceholder')}
                  value={formData.label || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
                  required
                />
              </div>

              {/* Description */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="description">
                  {t('resources.description')}
                </label>
                <textarea
                  id="description"
                  className={styles.formTextarea}
                  placeholder={t('resources.descriptionPlaceholder')}
                  value={formData.description || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>

              {/* Category */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="category">
                  {t('resources.category')}
                </label>
                <select
                  id="category"
                  className={styles.formSelect}
                  value={formData.category || sortedCategories[0]}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, category: e.target.value as ResourceCategory }))
                  }
                >
                  {sortedCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {tKpi(`preflight.categories.${cat}`)}
                    </option>
                  ))}
              </select>
              </div>

              {/* ── Certification fields (inline, only when category is certifications) ── */}
              {formData.category === 'certifications' && (
                <>
                  <span className={styles.formHint}>{t('resources.certificationHint')}</span>

                  {/* Certification / License Number */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="certificationNumber">
                      {t('resources.certificationNumber')}
                    </label>
                    <input
                      id="certificationNumber"
                      className={styles.formInput}
                      type="text"
                      maxLength={100}
                      value={formData.certification_number ?? ''}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, certification_number: e.target.value || null }))
                      }
                      placeholder={t('resources.certificationNumberPlaceholder')}
                    />
                  </div>

                  {/* Issue Date + Expiry Date side-by-side */}
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel} htmlFor="certificationDate">
                        {t('resources.certificationDate')}
                      </label>
                      <input
                        id="certificationDate"
                        className={styles.formInput}
                        type="date"
                        value={formData.certification_date ? formData.certification_date.substring(0, 10) : ''}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, certification_date: e.target.value || null }))
                        }
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel} htmlFor="certificationExpiry">
                        {t('resources.certificationExpiry')}
                      </label>
                      <input
                        id="certificationExpiry"
                        className={styles.formInput}
                        type="date"
                        value={formData.certification_expiry ? formData.certification_expiry.substring(0, 10) : ''}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, certification_expiry: e.target.value || null }))
                        }
                      />
                      <span className={styles.formHint}>{t('resources.certificationExpiryHint')}</span>
                    </div>
                  </div>

                  {/* PII warning for photo uploads */}
                  <span className={styles.validationHint}>{t('resources.certificationPiiWarning')}</span>
                </>
              )}

              {/* Offer Type */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{t('resources.offerType')}</label>
                <div className={styles.toggleGroup}>
                  {OFFER_TYPES.map((ot) => (
                    <button
                      key={ot}
                      type="button"
                      className={`${styles.toggleBtn} ${formData.offer_type === ot ? styles.toggleBtnActive : ''}`}
                      onClick={() => setFormData((prev) => ({ ...prev, offer_type: ot as OfferType }))}
                    >
                      {t(`resources.offerTypes.${ot}`)}
                    </button>
                  ))}
                </div>
                {formData.offer_type && (
                  <span className={styles.formHint}>
                    {t(`resources.offerTypeHints.${formData.offer_type}`)}
                  </span>
                )}
              </div>

              {/* Availability Status + Emergency */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{t('resources.availabilityStatus')}</label>
                <div className={styles.toggleGroup}>
                  {MODAL_STATES.map((st) => {
                    // Status buttons show active unless emergency_only is on
                    const isActive = formData.state === st && !formData.emergency_only;
                    let activeClass = '';
                    if (isActive) {
                      switch (st) {
                        case 'available': activeClass = styles.statusBtnAvailable; break;
                        case 'reserved': activeClass = styles.statusBtnReserved; break;
                        case 'unavailable': activeClass = styles.statusBtnUnavailable; break;
                        case 'retired': activeClass = styles.statusBtnRetired; break;
                      }
                    }
                    return (
                      <button
                        key={st}
                        type="button"
                        className={`${styles.statusBtn} ${isActive ? activeClass : ''}`}
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            state: st as UserResourceState,
                            // If emergency_only was on, turning on a normal status disables it
                            emergency_only: false,
                          }))
                        }
                      >
                        {t(`resources.states.${st}`)}
                      </button>
                    );
                  })}
                  {/* Emergency Response — orthogonal flag, does NOT deselect status */}
                  <button
                    type="button"
                    className={`${styles.statusBtn} ${formData.emergency_available ? styles.statusBtnEmergency : ''}`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        emergency_available: !prev.emergency_available,
                        // When disabling emergency, clear sub-fields
                        ...(prev.emergency_available
                          ? { emergency_only: false, deployment_time_minutes: null }
                          : {}),
                      }))
                    }
                  >
                    {t('resources.emergencySection')}
                  </button>
                </div>
              </div>

              {/* Emergency sub-fields — appear when Emergency Response is active */}
              {formData.emergency_available && (
                <div className={styles.emergencyContainer}>
                  {/* Emergency Only toggle */}
                  <div className={styles.emergencyRow}>
                    <div>
                      <div className={styles.emergencyLabel}>{t('resources.emergencyOnly')}</div>
                      <div className={styles.emergencyHint}>{t('resources.emergencyOnlyHint')}</div>
                    </div>
                    <button
                      type="button"
                      className={`${styles.toggleSwitch} ${formData.emergency_only ? styles.toggleSwitchActive : ''}`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          emergency_only: !prev.emergency_only,
                          // When turning ON emergency_only, deselect normal status
                          ...(!prev.emergency_only ? { state: 'unavailable' as UserResourceState } : { state: 'available' as UserResourceState }),
                        }))
                      }
                      role="switch"
                      aria-checked={formData.emergency_only ?? false}
                    >
                      <span
                        className={`${styles.toggleSwitchKnob} ${formData.emergency_only ? styles.toggleSwitchKnobActive : ''}`}
                      />
                    </button>
                  </div>

                  <hr className={styles.emergencySeparator} />

                  {/* Deployment time with unit picker */}
                  <div className={styles.formGroup}>
                    <label className={styles.emergencyLabel} htmlFor="deploymentTime">
                      {t('resources.deploymentTime')}
                    </label>
                    <span className={styles.emergencyHint}>{t('resources.deploymentTimeHint')}</span>
                    <div className={styles.durationPicker}>
                      <input
                        id="deploymentTime"
                        type="number"
                        className={`${styles.formInput} ${styles.durationInput}`}
                        min="1"
                        max="999"
                        value={formData.deployment_time_minutes ?? ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            deployment_time_minutes: e.target.value ? parseInt(e.target.value, 10) : null,
                          }))
                        }
                      />
                      <div className={styles.durationUnitGroup}>
                        {DEPLOYMENT_TIME_UNITS.map((u) => (
                          <button
                            key={u}
                            type="button"
                            className={`${styles.durationUnitBtn} ${
                              (formData.deployment_time_unit || 'minutes') === u
                                ? styles.durationUnitBtnActive
                                : ''
                            }`}
                            onClick={() =>
                              setFormData((prev) => ({ ...prev, deployment_time_unit: u }))
                            }
                          >
                            {t(`resources.deploymentTimeUnits.${u}`)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <hr className={styles.emergencySeparator} />

                  {/* Emergency notes */}
                  <div className={styles.formGroup}>
                    <label className={styles.emergencyLabel} htmlFor="emergencyNotes">
                      {t('resources.emergencyNotes')}
                    </label>
                    <span className={styles.emergencyHint}>{t('resources.emergencyNotesHint')}</span>
                    <textarea
                      id="emergencyNotes"
                      className={styles.formInput}
                      rows={3}
                      value={formData.emergency_notes ?? ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          emergency_notes: e.target.value || null,
                        }))
                      }
                      placeholder={t('resources.emergencyNotesPlaceholder')}
                    />
                  </div>
                </div>
              )}
            </>
          ), true)}

          {/* ── Section: Sharing ── */}
          {renderSection('sharing', 'resources.sectionSharing', false, (
            <>
              <span className={styles.formHint}>{t('resources.sharingHint')}</span>
              {communities.length > 0 ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className={styles.communitySelectAll}
                      onClick={selectAllCommunities}
                    >
                      {t('resources.selectAll')}
                    </button>
                    <button
                      type="button"
                      className={styles.communitySelectAll}
                      onClick={deselectAllCommunities}
                    >
                      {t('resources.deselectAll')}
                    </button>
                  </div>
                  <div className={styles.communityList}>
                    {communities.map((c) => {
                      const checked = isCommunitySelected(c.guid);
                      return (
                        <div
                          key={c.guid}
                          className={`${styles.communityRow} ${checked ? styles.communityRowChecked : ''}`}
                          onClick={() => toggleCommunity(c.guid)}
                          role="checkbox"
                          aria-checked={checked}
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleCommunity(c.guid); } }}
                        >
                          <div className={`${styles.communityCheckbox} ${checked ? styles.communityCheckboxChecked : ''}`}>
                            {checked ? '✓' : ''}
                          </div>
                          <span className={styles.communityName}>{c.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <span className={styles.formHint}>{t('resources.sharingRequired')}</span>
              )}
            </>
          ))}

          {/* ── Section: Photos ── */}
          {renderSection('photos', 'resources.sectionPhotos', false, (
            <>
              <span className={styles.formHint}>{t('resources.photosHint')}</span>
              <div className={styles.photoGrid}>
                {(formData.photos || []).map((photo, idx) => (
                  <div
                    key={idx}
                    className={`${styles.photoThumb} ${idx === 0 ? styles.photoThumbPrimary : ''} ${dragOverIdx === idx ? styles.photoThumbDragOver : ''}`}
                    draggable
                    onDragStart={() => handlePhotoDragStart(idx)}
                    onDragOver={(e) => handlePhotoDragOver(e, idx)}
                    onDrop={() => handlePhotoDrop(idx)}
                    onDragEnd={() => setDragOverIdx(null)}
                  >
                    <img src={photo.url} alt={`${t('resources.photos')} ${idx + 1}`} />
                    {idx === 0 && (
                      <span className={styles.photoPrimaryBadge}>{t('resources.photosPrimary')}</span>
                    )}
                    <button
                      type="button"
                      className={styles.photoRemoveBtn}
                      onClick={() => handlePhotoRemove(idx)}
                      aria-label="Remove photo"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {(formData.photos || []).length < 3 && (
                  <button
                    type="button"
                    className={styles.photoAddBtn}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    +
                    <span className={styles.photoAddLabel}>{t('resources.photosAdd')}</span>
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handlePhotoAdd}
              />
            </>
          ))}

          {/* ── Section: Lending Terms ── */}
          {renderSection('lendingTerms', 'resources.sectionLendingTerms', false, (
            <div className={styles.formGroup}>
              <textarea
                id="conditions"
                className={styles.formTextarea}
                placeholder={t('resources.conditionsPlaceholder')}
                value={formData.conditions || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, conditions: e.target.value }))}
                rows={3}
              />
              <span className={styles.formHint}>{t('resources.conditionsHint')}</span>
            </div>
          ))}

        </form>
      </Tray.Body>

      <Tray.Footer>
        {/* Pinned footer — Tray keeps it below the scrollable body */}
        <div className={styles.formActions}>
          {/* Delete — left-aligned, only when editing existing resource */}
          {resource?.resource_guid && onDelete ? (
            deleteConfirm ? (
              <div className={styles.deleteConfirmGroup}>
                <span className={styles.deleteConfirmLabel}>{t('resources.deleteConfirm')}</span>
                <button
                  type="button"
                  className={styles.modalDeleteButton}
                  onClick={() => { onDelete(resource.resource_guid!); onClose(); }}
                >
                  {t('resources.deleteResource')}
                </button>
                <button
                  type="button"
                  className={styles.deleteConfirmCancel}
                  onClick={() => setDeleteConfirm(false)}
                >
                  {t('resources.cancelEdit')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={styles.modalDeleteTrigger}
                onClick={() => setDeleteConfirm(true)}
              >
                {t('resources.deleteResource')}
              </button>
            )
          ) : (
            <div />
          )}

          <div className={styles.formActionsPrimary}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              {t('resources.cancelEdit')}
            </button>
            <button
              type="submit"
              form="resourceEditForm"
              className={styles.saveButton}
              disabled={!formData.label?.trim()}
            >
              {resource?.resource_guid ? t('resources.saveChanges') : t('resources.saveResource')}
            </button>
          </div>
        </div>
      </Tray.Footer>
    </Tray>
  );
}

/* ================================================================== */
/*  Helpers — card display                                             */
/* ================================================================== */

/** Category → emoji icon for cards without photos. */
const CATEGORY_ICONS: Record<string, string> = {
  materials: '\u{1F4E6}',
  equipment: '\u{1F527}',
  services: '\u{1F91D}',
  labor: '\u{1F477}',
  transportation: '\u{1F68C}',
  facilities: '\u{1F3E2}',
  admin_legal: '\u{1F4CB}',
  knowledge: '\u{1F4DA}',
  financial: '\u{1F4B0}',
  in_kind: '\u{1F381}',
  software_tech: '\u{1F4BB}',
  certifications: '\u{1F4DC}',
  other: '\u{1F4CE}',
};

/** Status → icon glyph for pill badge. */
const STATUS_ICONS: Record<string, string> = {
  available: '\u2713',
  reserved: '\u{1F512}',
  committed: '\u{1F4CC}',
  unavailable: '\u2014',
  retired: '\u{1F4E6}',
};

/** Deployment time unit options for the duration picker. */
const DEPLOYMENT_TIME_UNITS = ['minutes', 'hours', 'days'] as const;

/** Human-readable deployment time, respecting the stored unit. */
function formatDeployTime(value: number | null | undefined, unit?: string | null): string | null {
  if (!value) return null;
  const u = unit || 'minutes';
  if (u === 'hours') return value === 1 ? '~1 hr' : `~${value} hr`;
  if (u === 'days') return value === 1 ? '~1 day' : `~${value} days`;
  // minutes (default / legacy)
  if (value < 60) return `~${value} min`;
  if (value < 1440) return `~${Math.round(value / 60)} hr`;
  return `~${Math.round(value / 1440)} day`;
}

/** Category → tinted background for icon placeholders (higher contrast). */
const CATEGORY_BG: Record<string, string> = {
  materials: 'rgba(234,179,8,0.12)',
  equipment: 'rgba(59,130,246,0.12)',
  services: 'rgba(16,185,129,0.12)',
  labor: 'rgba(249,115,22,0.12)',
  transportation: 'rgba(139,92,246,0.12)',
  facilities: 'rgba(236,72,153,0.12)',
  admin_legal: 'rgba(107,114,128,0.12)',
  knowledge: 'rgba(14,165,233,0.12)',
  financial: 'rgba(234,179,8,0.15)',
  in_kind: 'rgba(244,63,94,0.12)',
  software_tech: 'rgba(99,102,241,0.12)',
  certifications: 'rgba(20,184,166,0.12)',
  other: 'rgba(107,114,128,0.10)',
};

/** Sort order: Available → Reserved → Committed → Unavailable → Retired */
const STATE_SORT_ORDER: Record<string, number> = {
  available: 0,
  reserved: 1,
  committed: 2,
  unavailable: 3,
  retired: 4,
};

/** Status-dot color by lifecycle state (management-tab status palette). */
const STATE_DOT_COLOR: Record<UserResourceState, string> = {
  available: 'var(--color-success)',
  reserved: 'var(--color-warning)',
  committed: 'var(--info-text)',
  unavailable: 'var(--text-muted)',
  retired: 'var(--text-muted)',
};

/* ================================================================== */
/*  ResourceCard                                                       */
/* ================================================================== */

interface ResourceCardProps {
  resource: UserResource;
  isSelected: boolean;
  onSelect: (resource: UserResource) => void;
  onStateChange: (newState: UserResourceState) => void;
  onEdit: () => void;
  onOffer?: () => void;
  canEdit: boolean;
  t: (key: string) => string;
  tKpi: (key: string) => string;
}

function ResourceCard({
  resource,
  isSelected,
  onSelect,
  onEdit,
  canEdit,
  t,
}: ResourceCardProps) {
  // Card classes — any emergency_available resource gets the emergency treatment
  const cardClasses = [
    styles.resourceCard,
    isSelected ? styles.resourceCardSelected : '',
    resource.emergency_available ? styles.resourceCardEmergency : '',
  ].filter(Boolean).join(' ');

  // Always compact: status dot + icon + name + edit. Selecting a resource
  // highlights the row and re-ranks the needs feed — there is no inline
  // detail expansion (removed: it was awkward and visually noisy).
  return (
    <div className={cardClasses}>
      <button
        className={styles.compactRow}
        onClick={() => onSelect(resource)}
        type="button"
        aria-label={`Select resource: ${resource.label}`}
      >
        <span
          className={styles.statusDot}
          style={{ backgroundColor: STATE_DOT_COLOR[resource.state] || 'var(--text-muted)' }}
        />
        <span className={styles.compactIcon}>{CATEGORY_ICONS[resource.category] || '\u{1F4CE}'}</span>
        <span className={styles.compactLabel}>{resource.label}</span>
        {canEdit && (
          <span
            className={styles.cardEditBtn}
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            role="button"
            tabIndex={0}
            aria-label={t('resources.editResource')}
            title={t('resources.editResource')}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z" />
            </svg>
          </span>
        )}
      </button>
    </div>
  );
}


/* ================================================================== */
/*  UserResourceRegistry (main component)                              */
/* ================================================================== */

export function UserResourceRegistry({
  userGuid,
  resources,
  onResourcesChange,
  onSave,
  onDelete,
  onResourceSelect,
  selectedResourceGuid,
  canEdit = true,
  hideHeader = false,
}: UserResourceRegistryProps) {
  const { t } = useAppTranslation('userProfile');
  const { t: tKpi } = useAppTranslation('kpi');
  const [editingResource, setEditingResource] = useState<EditingResource | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const prevManageOpenRef = useRef(false);

  /* ── Resolve user's member communities for sharing checklist ── */

  // communityMembershipStore is a mutable global array hydrated asynchronously.
  // Bump a version counter when it changes so useMemo re-computes.
  const [membershipVersion, setMembershipVersion] = useState(0);
  useEffect(() => {
    const bump = () => setMembershipVersion((v) => v + 1);
    window.addEventListener('l4e_community_membership_changed', bump);
    // Recompute when the community name/parent maps refresh too — otherwise a
    // first render before the maps populated leaves every community stuck on
    // its GUID fallback instead of its name.
    window.addEventListener('l4e_community_maps_changed', bump);
    // Also bump once on mount in case the store was already hydrated
    if (communityMembershipStore.length > 0) bump();
    return () => {
      window.removeEventListener('l4e_community_membership_changed', bump);
      window.removeEventListener('l4e_community_maps_changed', bump);
    };
  }, []);

  const communities = useMemo<CommunityOption[]>(() => {
    // membershipVersion is a dependency to trigger re-computation
    void membershipVersion;
    const upperUser = userGuid.toUpperCase();
    const result: CommunityOption[] = [];
    const seen = new Set<string>();

    for (const m of communityMembershipStore) {
      if (
        String(m.user_guid || '').toUpperCase() === upperUser &&
        String(m.status || 'active').toLowerCase() === 'active'
      ) {
        const guid = String(m.community_entity_guid || '');
        const upperGuid = guid.toUpperCase();
        if (!guid || seen.has(upperGuid)) continue;
        seen.add(upperGuid);

        // resolveCommunityPathNames returns root-to-leaf, excluding Earth,
        // e.g. ["United States", "Massachusetts", "Boston"]
        const pathNames = resolveCommunityPathNames(guid);
        // Reverse for leaf-first display: "Boston, Massachusetts, United States"
        const fullPath = pathNames.length > 0
          ? [...pathNames].reverse().join(', ')
          : guid;

        result.push({ guid, name: fullPath });
      }
    }

    result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [userGuid, membershipVersion]);

  // Track modal open transitions
  useEffect(() => {
    prevManageOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  const handleAddResource = useCallback(() => {
    setEditingResource(null);
    setIsModalOpen(true);
  }, []);

  const handleEditResource = useCallback((resource: UserResource) => {
    setEditingResource(resource);
    setIsModalOpen(true);
  }, []);

  const handleSaveResource = useCallback(
    async (saved: UserResource) => {
      const existingIndex = resources.findIndex((r) => r.resource_guid === saved.resource_guid);
      let updated: UserResource[];

      if (existingIndex >= 0) {
        updated = [...resources];
        updated[existingIndex] = saved;
      } else {
        updated = [...resources, saved];
      }

      onResourcesChange(updated);
      setIsModalOpen(false);
      setEditingResource(null);

      if (onSave) {
        try {
          await onSave(saved);
        } catch (err) {
          console.error('Failed to persist resource:', err);
        }
      }
    },
    [resources, onResourcesChange, onSave]
  );

  const handleDeleteResource = useCallback(
    async (resourceGuid: string) => {
      const updated = resources.filter((r) => r.resource_guid !== resourceGuid);
      onResourcesChange(updated);
      setIsModalOpen(false);
      setEditingResource(null);

      if (onDelete) {
        try {
          await onDelete(resourceGuid);
        } catch (err) {
          console.error('Failed to delete resource:', err);
        }
      }
    },
    [resources, onResourcesChange, onDelete]
  );

  const handleStateChange = useCallback(
    async (resourceGuid: string, newState: UserResourceState) => {
      const resource = resources.find((r) => r.resource_guid === resourceGuid);
      if (!resource) return;

      if (isValidUserResourceTransition(resource.state, newState)) {
        const updatedResource = { ...resource, state: newState, updated_at: new Date().toISOString() };
        const updated = resources.map((r) =>
          r.resource_guid === resourceGuid ? updatedResource : r
        );
        onResourcesChange(updated);

        if (onSave) {
          try {
            await onSave(updatedResource);
          } catch (err) {
            console.error('Failed to persist state change:', err);
          }
        }
      }
    },
    [resources, onResourcesChange, onSave]
  );

  /* ── Status filter chips state ── */
  const [visibleGroups, setVisibleGroups] = useState<Record<string, boolean>>({
    available: true,
    reserved: true,
    committed: true,
    unavailable: true,
    retired: true,
    emergency: true,
  });

  const toggleGroup = useCallback((key: string) => {
    setVisibleGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Counts per status group.
  // Emergency count = all resources with emergency_available (includes emergency_only).
  // emergency_only resources are excluded from their state count (they ONLY appear in the emergency segment).
  // emergency_available (but NOT emergency_only) resources count in BOTH their state AND emergency.
  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = {
      available: 0, reserved: 0, committed: 0, unavailable: 0, retired: 0, emergency: 0,
    };
    for (const r of resources) {
      if (r.emergency_available) {
        counts.emergency++;
      }
      if (!r.emergency_only) {
        counts[r.state] = (counts[r.state] || 0) + 1;
      }
    }
    return counts;
  }, [resources]);

  // Filter and sort visible resources.
  // - emergency_only resources appear ONLY when the emergency filter is on.
  // - emergency_available (not emergency_only) resources appear when their state
  //   filter is on OR the emergency filter is on (no duplicates).
  const filteredResources = useMemo(() => {
    const visible = resources.filter((r) => {
      const stateMatch = !r.emergency_only && visibleGroups[r.state];
      const emergencyMatch = r.emergency_available && visibleGroups.emergency;
      return stateMatch || emergencyMatch;
    });

    // Deduplicate (a resource matching both state and emergency should appear once)
    const seen = new Set<string>();
    const deduped = visible.filter((r) => {
      if (seen.has(r.resource_guid)) return false;
      seen.add(r.resource_guid);
      return true;
    });

    deduped.sort((a, b) => {
      // emergency_only items sort last
      if (a.emergency_only !== b.emergency_only) return a.emergency_only ? 1 : -1;
      const sa = STATE_SORT_ORDER[a.state] ?? 99;
      const sb = STATE_SORT_ORDER[b.state] ?? 99;
      if (sa !== sb) return sa - sb;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });

    return deduped;
  }, [resources, visibleGroups]);

  const hasResources = resources.length > 0;

  if (!hasResources && !canEdit) {
    return null;
  }

  // Segmented bar filter definitions
  const FILTER_SEGMENTS: { key: string; labelKey: string; color: string }[] = [
    { key: 'available', labelKey: 'resources.states.available', color: 'var(--color-success)' },
    { key: 'reserved', labelKey: 'resources.states.reserved', color: 'var(--color-warning)' },
    { key: 'committed', labelKey: 'resources.states.committed', color: 'var(--info-text)' },
    { key: 'unavailable', labelKey: 'resources.states.unavailable', color: 'var(--text-muted)' },
    { key: 'retired', labelKey: 'resources.states.retired', color: 'var(--text-muted)' },
    { key: 'emergency', labelKey: 'resources.filterEmergency', color: 'var(--color-error)' },
  ];

  // Dashed "Add a resource" card — sits below the status filters (above the
  // list); when the list is empty there are no filters, so it sits above the
  // empty state instead.
  const addResourceCard = canEdit ? (
    <button type="button" className={styles.addCard} onClick={handleAddResource}>
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <line x1="8" y1="3" x2="8" y2="13" />
        <line x1="3" y1="8" x2="13" y2="8" />
      </svg>
      {t('resources.addResource', 'Add a resource')}
    </button>
  ) : null;

  return (
    <div className={styles.container}>
      {/* Header — panel bar pattern (hidden when a host supplies the chrome,
          e.g. the Resources Rail). Add lives in the dashed card below. */}
      {!hideHeader && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerIcon}>🎒</span>
            <h2 className={styles.title}>{t('resources.title')}</h2>
          </div>
          <div className={styles.headerRight}>
            {resources.length > 0 && (
              <span className={styles.headerBadge}>
                {resources.length} {resources.length === 1 ? t('resources.resourceSingular') : t('resources.resourcePlural')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Empty state — no filters here, so the Add card sits above it. */}
      {!hasResources && (
        <>
          {addResourceCard}
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📦</div>
            <p className={styles.emptyTitle}>{t('resources.noResources')}</p>
            <p className={styles.emptyHint}>{t('resources.noResourcesHint')}</p>
          </div>
        </>
      )}

      {/* Status filter chips + flat resource list */}
      {hasResources && (
        <>
          {/* Segmented filter bar */}
          <div className={styles.segmentedBar}>
            {FILTER_SEGMENTS.map((seg) => {
              const active = visibleGroups[seg.key];
              const count = groupCounts[seg.key] || 0;
              return (
                <button
                  key={seg.key}
                  type="button"
                  className={`${styles.segmentBtn} ${active ? styles.segmentBtnActive : ''}`}
                  style={{ '--seg-color': seg.color } as React.CSSProperties}
                  onClick={() => toggleGroup(seg.key)}
                  disabled={count === 0}
                >
                  <span className={styles.segmentCount}>{count}</span>
                  <span className={styles.segmentLabel}>{t(seg.labelKey)}</span>
                  {active && <span className={styles.segmentAccent} />}
                </button>
              );
            })}
          </div>

          {/* Add card — below the status filters, above the list. */}
          {addResourceCard}

          {/* Flat resource list */}
          <div className={styles.resourcesList}>
            {filteredResources.map((resource) => (
              <ResourceCard
                key={resource.resource_guid}
                resource={resource}
                isSelected={selectedResourceGuid === resource.resource_guid}
                onSelect={(res) =>
                  onResourceSelect?.(
                    selectedResourceGuid === res.resource_guid ? null : res
                  )
                }
                onStateChange={(newState) =>
                  handleStateChange(resource.resource_guid, newState)
                }
                onEdit={() => handleEditResource(resource)}
                canEdit={canEdit}
                t={t}
                tKpi={tKpi}
              />
            ))}
          </div>
        </>
      )}

      {/* Edit modal */}
      <ResourceEditModal
        resource={editingResource}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingResource(null);
        }}
        onSave={handleSaveResource}
        onDelete={(guid) => handleDeleteResource(guid)}
        userGuid={userGuid}
        communities={communities}
        t={t}
        tKpi={tKpi}
      />

    </div>
  );
}
